import { Button, FormGroup, H4 } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { IFieldProps } from "decorator-form";
import React from "react";
import styled, { css } from "styled-components";

import ArrayItemToolbar from "common/form/common/ArrayItemToolbar";
import { ClearWidget, DisplayWidget, FloatWidget, TextAlignWidget } from "common/form/widgets/BlockWidgets";
import DropdownSelect from "common/form/widgets/common/DropdownSelect";
import CssUnitWidget from "common/form/widgets/CssUnitWidget";
import VerticalCenter from "common/fragments/VerticalCenter";
import { ILayoutType, LayoutTypeRegistry } from "../ILayoutType";

interface IBlockItem {
	display?: 'block' | 'inline-block';
	float?: 'none' | 'left' | 'right';
	clear?: 'none' | 'left' | 'right' | 'both';
	textAlign?: 'left' | 'center' | 'right';
	maxWidth?: string;
	regionUuid?: string;
}

interface IBlockLayout {
	regions: IBlockItem[];
	gap: string;
}

const BlockWrapper = styled.div`
	overflow: hidden;

	&::before,
	&::after {
		content: "";
		display: block;
		clear: both;
	}
`;

const BlockItem = styled.div<{gap?: string}>`
	${(props) => props.gap && css`
		margin-bottom: ${props.gap};

		&:last-child {
			margin-bottom: 0;
		}
	`}
`;

const styles = {
	wrapper: {
		backgroundColor: "rgba(0,0,0,0.2)",
		border: "1px solid rgba(0,0,0,0.3)",
		minHeight: "200px"
	},
	item: {
		backgroundColor: "rgba(19,124,189,0.2)",
		border: "1px solid rgba(19,124,189,0.3)",
		borderRadius: "4px",
		color: "rgb(19,124,189)",
		minHeight: "30px"
	}
};

class BlockLayoutField extends React.Component<IFieldProps<IBlockLayout> & { regions: { [uuid: string]: { __editName: string; } } }> {

	public render() {
		const { formData = {} as IBlockLayout, regions } = this.props;

		return (
			<FormGroup>
				<FormGroup label="Gap / Spacing">
					<CssUnitWidget onChange={(gap) => this.partialChange({gap})} value={formData.gap} />
				</FormGroup>
				<FormGroup>
						{
							formData.regions && formData.regions.map((region, ri) => (
								<FormGroup key={ri}>
									<div style={{display: 'flex', justifyContent: 'space-between'}}>
										<H4>{`Region ${ri + 1}`}</H4>
										<ArrayItemToolbar itemIndex={ri} arr={formData.regions} onChange={this.onChangeRegions} />
									</div>
									<FormGroup label="Region">
										<DropdownSelect
											value={region.regionUuid}
											onChange={this.onChangeRegionField(ri, 'regionUuid')}
											options={Object.entries(regions || {}).map(([uuid, reg]) => ({
												value: uuid,
												label: reg.__editName || "Untitled Region"
											}))}
										/>
									</FormGroup>
									<FormGroup label="Display">
										<DisplayWidget value={region.display} onChange={this.onChangeRegionField(ri, 'display')} />
									</FormGroup>
									<FormGroup label="Float">
										<FloatWidget value={region.float} onChange={this.onChangeRegionField(ri, 'float')} />
									</FormGroup>
									<FormGroup label="Clear">
										<ClearWidget value={region.clear} onChange={this.onChangeRegionField(ri, 'clear')} />
									</FormGroup>
									<FormGroup label="Text Align">
										<TextAlignWidget value={region.textAlign} onChange={this.onChangeRegionField(ri, 'textAlign')} />
									</FormGroup>
									<FormGroup label="Max Width">
										<CssUnitWidget value={region.maxWidth} onChange={this.onChangeRegionField(ri, 'maxWidth')} />
									</FormGroup>
								</FormGroup>
							))
						}
						<FormGroup>
							<Button small={true} text="Add Region" onClick={this.addRegion} />
						</FormGroup>
					</FormGroup>
			</FormGroup>
		);
	}

	private addRegion = () => {
		const { formData = {} as IBlockLayout, onChange } = this.props;
		onChange({...formData, regions: [...formData.regions || [], {}]});
	}

	private onChangeRegions = (regions: IBlockItem[]) => {
		const { formData = {} as IBlockLayout, onChange } = this.props;
		onChange({...formData, regions});
	}

	private onChangeRegionField = (index: number, field: string) => (value: any) => {
		const { formData = {} as IBlockLayout, onChange } = this.props;
		onChange({
			...formData,
			regions: formData.regions.map((r, ri) => ri === index ? {...r, [field]: value} : r)
		});
	}

	private partialChange = (value: Partial<IBlockLayout>) => {
		const { onChange, formData = {} as IBlockLayout } = this.props;
		onChange({
			...formData,
			...value
		});
	}
}

const BlockLayout: ILayoutType<IBlockLayout> = {
	type: 'block',
	label: 'Block',
	icon: IconNames.LIST,
	renderInput: (data, onChange, regions) => (
		<BlockLayoutField formData={data} onChange={onChange} regions={regions} uiSchema={undefined} idSchema={undefined} />
	),
	renderPreview: (data, regions) => (
		<BlockWrapper style={styles.wrapper}>
			{
				data.regions && data.regions.map(({regionUuid, ...restRegion}, ri) => (
					<VerticalCenter key={ri} style={{...restRegion, ...styles.item}}>
						{
							regionUuid ? (
								regions[regionUuid] && regions[regionUuid].__editName || "Invalid Region!"
							) : "(Select a Region)"
						}
					</VerticalCenter>
				))
			}
		</BlockWrapper>
	),
	renderLayout: ({regions, gap}, renderRegion) => (
		<BlockWrapper>
			{
				regions && regions.map(({regionUuid, ...restRegion}, index) => (
					<BlockItem key={index} style={restRegion} gap={gap}>
						{regionUuid && renderRegion(regionUuid, index)}
					</BlockItem>
				))
			}
		</BlockWrapper>
	)
};

export default LayoutTypeRegistry.registerType(BlockLayout);



// WEBPACK FOOTER //
// ./src/common/form/fields/layout/layoutTypes/BlockLayout.tsx